import backendProviderService from "./backend-provider-service.js";

/**
 * BackendProviderWrapper - Wraps the backend service to maintain the same interface as original providers
 * This allows us to replace provider instances without changing the calling code
 */
class BackendProviderWrapper {
  constructor(providerName, options = {}) {
    this.providerName = providerName;
    this.options = options;
    this.defaultModel =
      options.defaultModel || this.getDefaultModelForProvider(providerName);
    this.backendService = backendProviderService;
  }

  /**
   * Get default model for a provider
   * @param {string} providerName - Name of the provider
   * @returns {string} - Default model name
   */
  getDefaultModelForProvider(providerName) {
    const defaults = {
      openai: "gpt-4.1-mini",
      anthropic: "claude-3-5-haiku-20241022",
      gemini: "gemini-2.5-flash",
      mistral: "mistral-large-latest",
      deepseek: "deepseek-chat",
      xai: "grok-3",
      groq: "llama-3.3-70b-versatile",
      cohere: "command-a-03-2025",
      perplexity: "sonar",
      qwen: "qwen-max-latest",
      openrouter: "openai/o1-mini",
    };
    return defaults[providerName] || "gpt-4.1-mini";
  }

  /**
   * Create a streaming chat completion
   * @param {Object} params - Parameters for the completion
   * @param {function} onUpdate - Callback function for each chunk of the stream
   * @param {Object} options - Additional options like AbortController signal
   * @returns {Promise<void>} - Promise that resolves when the stream is complete
   */
  async createChatCompletionStream(params, onUpdate, options = {}) {
    // Add provider and default model to params
    const backendParams = {
      provider: this.providerName,
      model: params.model || this.defaultModel,
      ...params,
    };

    return this.backendService.createChatCompletionStream(
      backendParams,
      onUpdate,
      options
    );
  }

  /**
   * Create a non-streaming chat completion
   * @param {Object} params - Parameters for the completion
   * @returns {Promise<Object>} - Promise resolving to completion response
   */
  async createChatCompletion(params) {
    // Add provider and default model to params
    const backendParams = {
      provider: this.providerName,
      model: params.model || this.defaultModel,
      ...params,
    };

    return this.backendService.createChatCompletion(backendParams);
  }

  /**
   * List available models from this provider
   * @returns {Promise<Array>} - Promise resolving to array of model objects
   */
  async listModels() {
    return this.backendService.listModels(this.providerName);
  }

  /**
   * Check if the provider is properly configured and available
   * @returns {Promise<boolean>} - Promise resolving to true if provider is available
   */
  async checkAvailability() {
    return this.backendService.checkAvailability(this.providerName);
  }

  /**
   * Format message history (compatibility method)
   * @param {Array} messages - Array of message objects
   * @param {string} instructions - Assistant instructions
   * @returns {Array} - Formatted messages
   */
  formatMessageHistory(messages, instructions) {
    return this.backendService.formatMessageHistory(messages, instructions);
  }

  /**
   * Check if a model is a reasoning model (compatibility method)
   * @param {string} modelName - The model name to check
   * @returns {boolean} - Whether the model is a reasoning model
   */
  isReasoningModel(modelName) {
    return this.backendService.isReasoningModel(modelName);
  }

  /**
   * Create a function call using the provider
   * @param {Object} params - Parameters for the function call
   * @returns {Promise<Object>} - Promise resolving to function call response
   */
  async createFunctionCall(params) {
    // For function calls, we use the same completion endpoint with tools
    const backendParams = {
      provider: this.providerName,
      model: params.model || this.defaultModel,
      ...params,
    };

    return this.backendService.createChatCompletion(backendParams);
  }

  /**
   * Upload a file (not yet implemented in backend)
   * @param {Object} params - Parameters for file upload
   * @returns {Promise<Object>} - Promise resolving to file upload response
   */
  async uploadFile(params) {
    throw new Error("File upload not yet implemented in backend service");
  }

  /**
   * Analyze an image (not yet implemented in backend)
   * @param {Object} params - Parameters for image analysis
   * @returns {Promise<Object>} - Promise resolving to image analysis response
   */
  async analyzeImage(params) {
    throw new Error("Image analysis not yet implemented in backend service");
  }

  /**
   * Retrieve file content (not yet implemented in backend)
   * @param {Object} params - Parameters for file retrieval
   * @returns {Promise<Object>} - Promise resolving to file retrieval response
   */
  async retrieveFileContent(params) {
    throw new Error("File retrieval not yet implemented in backend service");
  }

  /**
   * Get provider-specific headers (compatibility method)
   * @returns {Object} - Headers object
   */
  getHeaders() {
    // This is handled by the backend now
    return {};
  }
}

export default BackendProviderWrapper;
